import NavigationHeader from 'components/molecules/navigation-header';
import SearchPatientsByType from 'components/organisms/search-patients-by-type';
import { isValidGuid } from 'infrastructure/functions';
import {
  useFollowUpTableDataQueryFilter,
  useNewlyTableDataQueryFilter,
  usePartialsTableDataQueryFilter,
  useSearchPatientQueryFilter,
} from 'infrastructure/hooks/ccm/console';
import BaseButton from 'components/atoms/baseButton';
import { useTranslation } from 'react-i18next';
import AgencyDoctorSelectBox from 'components/molecules/agency-doctor-select-box';
import { RoleGroups } from 'infrastructure/consts';
import useUser from 'utils/useUser';

import s from './styles.module.scss';
import { useSearch } from './use-search';

import type { FC } from 'react';
import type { INavigationHeaderProps } from 'components/molecules/navigation-header';

interface IProps extends INavigationHeaderProps {}

const CcmConsoleNavigationHeader: FC<IProps> = ({ title }) => {
  const {
    dataOptions,
    debounceSearch,
    isLoading,
    placeholder,
    options,
    searchKey,
    searchValue,
    DEFAULT_FILTER,
    setSearchValue,
    onSelect,
    onClear,
    onTypeChange,
    appendFilters,
    ccmCaseManagerGuid,
  } = useSearch();

  const { isSupervisorRole } = useUser();

  const { count: patientCount, resetFilter: resetPatientFilter } =
    useSearchPatientQueryFilter();
  const { count: newlyCount, resetFilter: resetNewlyFilter } =
    useNewlyTableDataQueryFilter();
  const { count: partialsCount, resetFilter: resetPartialsFilter } =
    usePartialsTableDataQueryFilter();
  const { count: followUpCount, resetFilter: resetFollowUpFilter } =
    useFollowUpTableDataQueryFilter();
  const { t } = useTranslation();

  const showResetBtn =
    patientCount > 0 ||
    newlyCount > 0 ||
    partialsCount > 0 ||
    followUpCount > 0;

  const onResetFilters = () => {
    setSearchValue('');
    resetPatientFilter();
    resetFollowUpFilter();
    resetNewlyFilter();
    resetPartialsFilter();
  };

  return (
    <NavigationHeader title={title} className={s.wrapper}>
      <div className={s['actions-wrapper']}>
        {showResetBtn && (
          <BaseButton
            type="secondary"
            onClick={onResetFilters}
            label={t('controls.resetFilters')}
            className={s['reset-btn']}
          />
        )}
        {isSupervisorRole && (
          <AgencyDoctorSelectBox
            value={ccmCaseManagerGuid}
            placeholder={t('labels.ccmSpecialists')}
            minWidth="200px"
            maxWidth="200px"
            roles={RoleGroups.CCMSpecialistRoles}
            allowClear
            maxTagCount={0}
            onChange={(c) =>
              appendFilters({
                ccmCaseManagerGuid: c,
              })
            }
          />
        )}
        <SearchPatientsByType
          typeOptions={options}
          defaultTypeValue={DEFAULT_FILTER.value}
          dataOptions={dataOptions ?? []}
          typeValue={searchKey}
          searchValue={searchValue}
          isLoading={isLoading}
          placeholder={placeholder}
          onTypeChange={onTypeChange}
          onSelect={onSelect}
          onSearch={(e) => {
            setSearchValue(e.target.value);
            const isGuid = searchKey === 'guid';
            if (isGuid && isValidGuid(e.target.value)) {
              debounceSearch({
                searchKey,
                searchValue: e.target.value,
              });
            } else if (!isGuid) {
              debounceSearch({
                searchKey,
                searchValue: e.target.value,
              });
            }
          }}
          searchWith={350}
          onClear={onClear}
        />
      </div>
    </NavigationHeader>
  );
};

export default CcmConsoleNavigationHeader;
